import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState, type FormEvent } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { LegalFooter } from "@/components/LegalPage";
import { submitTeamInquiry } from "@/lib/teams.functions";

const MONO = '"JetBrains Mono", monospace';
const SERIF = '"Cormorant Garamond", serif';

export const Route = createFileRoute("/teams")({
  head: () => {
    const title = "WHO KNEW for teams — bulk licenses for organisations";
    const description =
      "Financial literacy training for non-finance teams. 29 self-paced lessons, real UK company case studies. Bulk pricing from 10 licenses.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: "/teams" },
      ],
    };
  },
  component: TeamsPage,
});

function Divider() {
  return (
    <div
      style={{
        marginTop: "48px",
        marginBottom: "48px",
        height: "1px",
        backgroundColor: "#8B1E1E",
        opacity: 0.35,
      }}
    />
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="font-bold uppercase tracking-[0.05em] text-burgundy"
      style={{ fontFamily: MONO, fontSize: "12px" }}
    >
      {children}
    </p>
  );
}

function Body({ children, mt = 20 }: { children: React.ReactNode; mt?: number }) {
  return (
    <p
      className="text-ink"
      style={{
        fontFamily: SERIF,
        fontSize: "18px",
        lineHeight: 1.6,
        marginTop: `${mt}px`,
      }}
    >
      {children}
    </p>
  );
}

function Muted({ children, italic = true }: { children: React.ReactNode; italic?: boolean }) {
  return (
    <p
      className={italic ? "italic" : ""}
      style={{
        fontFamily: SERIF,
        fontSize: "16px",
        color: "#5A534A",
        lineHeight: 1.6,
        marginTop: "16px",
      }}
    >
      {children}
    </p>
  );
}

function TeamsPage() {
  const submit = useServerFn(submitTeamInquiry);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "submitting") return;
    setStatus("submitting");
    setErrorMsg(null);
    try {
      const result = await submit({ data: { email, website: "" } });
      if (result.ok) {
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMsg(result.error);
      }
    } catch {
      setStatus("error");
      setErrorMsg("Something went wrong. Please email hello@whoknew.guide directly.");
    }
  }

  const bullets = [
    "Non-finance managers who feel they should probably know this",
    "Marketing, sales, ops, and product teams who sit through finance meetings",
    "Founders training their next layer of leadership",
    'The graduate who recently described EBITDA as "something to do with profit, I think"',
  ];

  const pricing: Array<[string, string, string?]> = [
    ["1-9 licenses", "£99"],
    ["10-49 licenses", "£79", "(20% off)"],
    ["50-99 licenses", "£69", "(30% off)"],
    ["100+ licenses", "£59", "(40% off)"],
  ];

  return (
    <div className="flex min-h-screen flex-col bg-paper text-ink">
      <SiteHeader />
      <main
        className="mx-auto w-full"
        style={{
          maxWidth: "720px",
          paddingLeft: "24px",
          paddingRight: "24px",
          paddingTop: "128px",
        }}
      >
        <Eyebrow>FOR TEAMS</Eyebrow>
        <h1
          className="text-[28px] md:text-[40px] font-bold leading-[1.15] text-ink"
          style={{ marginTop: "24px" }}
        >
          <span className="text-burgundy">WHO KNEW</span> for organisations.
        </h1>
        <div
          style={{
            marginTop: "24px",
            width: "60px",
            height: "2px",
            backgroundColor: "#8B1E1E",
          }}
        />

        <div style={{ marginTop: "48px" }}>
          <Body mt={0}>
            Most of your non-finance managers cannot read your P&amp;L. The marketing director runs
            brilliant campaigns but couldn't tell you whether they made money. The ops lead fixes
            supply chains but doesn't know why margins moved last quarter. The new graduate, asked
            about cash flow in their first week, confidently explains it's "the bit at the end of
            the year where you find out how much profit you made." It is not.
          </Body>
          <Body>
            <span className="text-burgundy">WHO KNEW</span> is the course for this. Twenty-nine self-paced lessons, most under five
            minutes, that teach financial literacy to anyone who reads — or makes decisions based
            on — your numbers. The worked examples are real UK companies: Burberry, JD Wetherspoon,
            Tesco, JD Sports, Greggs. Your team finishes the course able to read your accounts and
            understand them. Their colleagues finish able to nod knowingly without panic. Bulk
            pricing starts at ten licenses.
          </Body>
        </div>

        <Divider />

        <Eyebrow>WHO IT'S FOR</Eyebrow>
        <ul
          className="text-ink"
          style={{
            fontFamily: SERIF,
            fontSize: "18px",
            lineHeight: 1.6,
            marginTop: "20px",
            listStyle: "none",
            padding: 0,
          }}
        >
          {bullets.map((b, i) => (
            <li
              key={i}
              style={{
                marginTop: i === 0 ? 0 : "12px",
                paddingLeft: "20px",
                textIndent: "-20px",
              }}
            >
              <span style={{ color: "#8B1E1E", marginRight: "10px" }}>·</span>
              {b}
            </li>
          ))}
        </ul>

        <Divider />

        <Eyebrow>PRICING</Eyebrow>
        <p
          className="italic"
          style={{
            fontFamily: SERIF,
            fontSize: "16px",
            color: "#5A534A",
            marginTop: "20px",
          }}
        >
          Per-license, billed once for lifetime access:
        </p>
        <div
          style={{
            marginTop: "20px",
            fontFamily: MONO,
            fontSize: "14px",
            color: "#1A1612",
          }}
        >
          {pricing.map(([tier, price, discount]) => (
            <div
              key={tier}
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(140px, 1fr) 80px 1fr",
                gap: "12px",
                padding: "8px 0",
                borderBottom: "1px dotted #E8E1D3",
              }}
            >
              <span>{tier}</span>
              <span style={{ fontWeight: 700, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {price}
              </span>
              <span style={{ color: "#5A534A" }}>{discount ?? ""}</span>
            </div>
          ))}
        </div>
        <Muted>
          Full course, workbooks, and templates included. Each licensed user gets their own login.
        </Muted>

        <Divider />

        <Eyebrow>WANT TO TRY IT FIRST?</Eyebrow>
        <Body>
          We'll send you a free code so you can take the full course before buying for your team.
        </Body>

        <div style={{ marginTop: "24px" }}>
          {status === "success" ? (
            <div
              role="status"
              aria-live="polite"
              style={{
                fontFamily: SERIF,
                fontSize: "18px",
                color: "#1A1612",
                padding: "20px 24px",
                background: "#FAF6EC",
                border: "1px solid #E8E1D3",
              }}
            >
              Got it. We'll be in touch within one working day.
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              noValidate={false}
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {/* honeypot */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1 }}
                aria-hidden="true"
              />
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  disabled={status === "submitting"}
                  aria-label="Your email"
                  style={{
                    flex: "1 1 240px",
                    minWidth: 0,
                    fontFamily: SERIF,
                    fontSize: "17px",
                    padding: "14px 16px",
                    background: "#FAF6EC",
                    border: "1px solid #C9C0AE",
                    color: "#1A1612",
                    outline: "none",
                  }}
                />
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="font-bold uppercase tracking-[0.1em]"
                  style={{
                    fontFamily: MONO,
                    fontSize: "12px",
                    background: "#8B1E1E",
                    color: "#FAF6EC",
                    padding: "14px 22px",
                    border: "none",
                    cursor: status === "submitting" ? "not-allowed" : "pointer",
                    opacity: status === "submitting" ? 0.7 : 1,
                  }}
                >
                  {status === "submitting" ? "SENDING…" : "REQUEST A CODE"}
                </button>
              </div>
              {status === "error" && errorMsg && (
                <p
                  role="alert"
                  style={{
                    fontFamily: SERIF,
                    fontSize: "15px",
                    color: "#8B1E1E",
                    marginTop: "4px",
                  }}
                >
                  {errorMsg}
                </p>
              )}
            </form>
          )}
          {status !== "success" && (
            <Muted>You'll receive it within one working day. Probably sooner.</Muted>
          )}
        </div>

        <Divider />

        <Eyebrow>BUY A TEAM PACKAGE</Eyebrow>
        <Body>
          Ten or more licenses: drop us a line and we'll send you a quote and a payment link.
        </Body>
        <p style={{ marginTop: "20px", fontFamily: SERIF, fontSize: "18px" }}>
          <a
            href="mailto:hello@whoknew.guide"
            className="font-bold underline text-burgundy hover:opacity-80"
            style={{ textUnderlineOffset: "3px" }}
          >
            hello@whoknew.guide
          </a>
        </p>

        <div style={{ paddingBottom: "96px" }} />
      </main>
      <LegalFooter />
    </div>
  );
}
